import { useState, useRef, useEffect } from "react";
import I18n from "common/i18n";
import { localeToHtmlLang } from "common/utils/locale";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { Select, SelectOption } from "common/elements/Select/Select";
import { Footer, TitleSvg } from "features/home";
import { AuthSelection } from "features/auth/AuthSelection";
import { CreateGuestUser } from "features/auth/CreateGuestUser";

const LOCALE_OPTIONS: SelectOption<string>[] = [
  { value: "ja", label: "ひらがな" },
  { value: "ja_kanji", label: "漢字" },
  { value: "en", label: "English" },
  { value: "zh_tw", label: "繁體中文" },
];

const LOCALE_LABELS: Record<string, string> = {
  ja: "ひらがな",
  ja_kanji: "漢字",
  en: "English",
  zh_tw: "繁體中文",
};

/**
 * デバイス/ブラウザの言語設定に基づいてデフォルトロケールを取得する
 * - ネイティブアプリ: window.kidsAppHostLanguageCode を使用
 * - ウェブ環境: navigator.language を使用
 * - デフォルトは日本語(ja)
 */
const getDefaultLocale = (): string => {
  // 開発環境のみen/zh_twが利用可能
  const availableLocales = ["ja", "ja_kanji", "en", "zh_tw"];

  const getLocaleFromLangCode = (lang: string): string => {
    const langCode = lang.split("-")[0].toLowerCase();
    if (langCode === "en" && availableLocales.includes("en")) {
      return "en";
    }
    if (langCode === "zh" && availableLocales.includes("zh_tw")) {
      return "zh_tw";
    }
    return "ja";
  };

  // ネイティブアプリの場合は、デバイス言語設定を使用
  if (window.kidsAppHostLanguageCode) {
    return getLocaleFromLangCode(window.kidsAppHostLanguageCode);
  }

  // ウェブ環境の場合はブラウザの言語設定を使用
  return getLocaleFromLangCode(navigator.language || "");
};

export const Onboarding = () => {
  const TITLE_WIDTH = 828;
  const TITLE_HEIGHT = 182;
  const ITEM_HEIGHT = 454;
  const ITEM_TOP_MARGIN = 28;

  const play = usePlaySound();
  const { scale } = useScale();
  const [guest, setGuest] = useState(false);
  const [signUp, setSignUp] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const [currentLocale, setCurrentLocale] = useState<string>(getDefaultLocale);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  // 初期ロード時にI18n.localeとCSSクラスを設定
  useEffect(() => {
    I18n.locale = currentLocale;

    const rootElement = document.documentElement;
    rootElement.className = rootElement.className
      .replace(/\blang-[a-z]{2}(_[a-z]{2})?\b/g, "")
      .trim();
    if (currentLocale && !currentLocale.includes("ja")) {
      rootElement.classList.add(`lang-${currentLocale}`);
    }
    rootElement.lang = localeToHtmlLang(currentLocale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowGuest = () => {
    btnDelay(() => {
      play();
      setGuest(true);
    });
  };
  const handleCloseGuest = () => {
    btnDelay(() => {
      play();
      setGuest(false);
    });
  };

  const handleShowAuthSelection = () => {
    btnDelay(() => {
      play();
      setSignUp(true);
    });
  };
  const handleCloseAuthSelection = () => {
    btnDelay(() => {
      play();
      setSignUp(false);
    });
  };

  const handleToggleLocale = () => {
    btnDelay(() => {
      play();
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    I18n.locale = locale;
    setCurrentLocale(locale);

    const rootElement = document.documentElement;
    rootElement.className = rootElement.className
      .replace(/\blang-[a-z]{2}(_[a-z]{2})?\b/g, "")
      .trim();
    if (locale && !locale.includes("ja")) {
      rootElement.classList.add(`lang-${locale}`);
    }
    rootElement.lang = localeToHtmlLang(locale);
  };

  return (
    <div className="flex-col-el flex-center relative w-full h-full">
      <CreateGuestUser open={guest} close={handleCloseGuest} />

      <AuthSelection open={signUp} close={handleCloseAuthSelection} />

      <div
        className="flex-col-view self-center"
        style={{
          width: TITLE_WIDTH * scale,
          height: TITLE_HEIGHT * scale,
        }}
      >
        <div
          className="flex-col-view origin-top-left"
          style={{
            width: TITLE_WIDTH,
            height: TITLE_HEIGHT,
            transform: `scale(${scale})`,
          }}
        >
          <TitleSvg />
        </div>
      </div>

      <div
        className="flex-col-center w-full"
        style={{
          height: ITEM_HEIGHT * scale,
          marginTop: ITEM_TOP_MARGIN * scale,
        }}
      >
        <div className="flex-row-center gap-8 upsilon" style={{ transform: `scale(${scale})` }}>
          <button
            className="btn btn-puzzle-blue w-[520px] h-[280px] !rounded-[24px]"
            onClick={handleShowGuest}
            style={{
              boxShadow: "0 8px 24px rgba(76, 157, 241, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div className="flex-col-center w-full h-full p-8 relative">
              <div className="flex-col-center mb-4">
                <div
                  className="flex-col-center bg-white rounded-[32px] w-[120px] h-[120px] shadow-lg border-4 border-blue-20"
                  style={{
                    boxShadow: "0 8px 16px rgba(76, 157, 241, 0.2)",
                  }}
                >
                  <span className="material-symbols-outlined !text-[64px] text-blue-100">
                    rocket_launch
                  </span>
                </div>
              </div>

              <div className="text-center">
                <p className="text-body text-3xl text-white">
                  {I18n.t("MSG_ONBOARDING_FIRST_TIME")}
                </p>
                <p className="text-body text-base text-white !my-4">
                  {I18n.t("MSG_ONBOARDING_FIRST_TIME_SUB")}
                </p>
              </div>
            </div>
          </button>

          <button
            className="btn btn-danger w-[520px] h-[280px] !rounded-[24px]"
            onClick={handleShowAuthSelection}
            style={{
              boxShadow: "0 8px 24px rgba(252, 101, 96, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div className="flex-col-center w-full h-full p-8 relative">
              <div className="flex-col-center mb-4">
                <div
                  className="flex-col-center bg-white rounded-[32px] w-[120px] h-[120px] shadow-lg border-4 border-red-60"
                  style={{
                    boxShadow: "0 8px 16px rgba(252, 101, 96, 0.2)",
                  }}
                >
                  <span className="material-symbols-outlined !text-[64px] text-red-100">
                    emoji_events
                  </span>
                </div>
              </div>

              <div className="text-center">
                <p className="text-body text-3xl text-white">
                  {I18n.t("MSG_ONBOARDING_EXISTING_USER")}
                </p>
                <p className="text-body text-base !my-4">
                  {I18n.t("MSG_ONBOARDING_EXISTING_USER_SUB")}
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div className="flex-col-center origin-top" style={{ transform: `scale(${scale})` }}>
        <div className="flex-row-el items-center gap-3">
          <span className="text-[20px] text-textcolor-black w-[200px] text-right">
            {I18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
          </span>
          <button
            ref={langBtnRef}
            onClick={handleToggleLocale}
            className="border-0 bg-green-100 flex-row-el items-center gap-2 px-6 py-3 rounded-full backdrop-blur-sm shadow-md cursor-pointer transition-all w-[200px]"
          >
            <span className="material-symbols-outlined text-white !text-[28px]">language</span>
            <span className="text-body text-white text-[20px] flex-1 text-center">
              {LOCALE_LABELS[currentLocale] || LOCALE_LABELS["ja"]}
            </span>
            <span className="material-symbols-outlined text-white !text-[20px]">expand_more</span>
          </button>
          <span className="w-[200px]" />
        </div>
      </div>

      <Select
        anchorRef={langBtnRef}
        open={localeOpen}
        onClose={handleCloseLocale}
        value={currentLocale}
        onChange={handleChangeLocale}
        options={LOCALE_OPTIONS}
      />

      <Footer />
    </div>
  );
};
